import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface QuestionCitationTooltipProps {
  citations: PrefillCitationDef;
  className?: string;
}

interface CitationListProps {
  title: string;
  citations: PrefillCitations[];
  variant: 'general' | 'modified';
}

function CitationList({ title, citations, variant }: CitationListProps) {
  if (citations.length === 0) return null;

  const variantStyles = {
    general: 'border-blue-200 bg-blue-50',
    modified: 'border-orange-200 bg-orange-50',
  };

  const badgeStyles = {
    general: 'bg-blue-100 text-blue-700',
    modified: 'bg-orange-100 text-orange-700',
  };

  return (
    <div className="mb-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold text-gray-900">{title}</span>
        <span
          className={cn(
            'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium',
            badgeStyles[variant],
          )}
        >
          {citations.length}
        </span>
      </div>
      <div className="space-y-2">
        {citations.map((citation, index) => (
          <div
            key={`${variant}-${index}`}
            className={cn('rounded-md border p-2 text-xs', variantStyles[variant])}
          >
            <div className="space-y-1">
              <div>
                <span className="font-semibold text-gray-700">Q:</span>{' '}
                <span className="text-gray-600">{citation.title}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">A:</span>{' '}
                <span className="text-gray-600">
                  {typeof citation.answer === 'boolean'
                    ? citation.answer
                      ? 'Yes'
                      : 'No'
                    : String(citation.answer).replace(/\|\|\|/g, ', ')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionCitationTooltip({ citations, className }: QuestionCitationTooltipProps) {
  const totalCitations =
    citations.generalCitations.length +
    citations.partnerCitations.length +
    citations.modifiedCitations.length;

  if (totalCitations === 0) return null;

  const hasModifiedSources = citations.modifiedCitations.length > 0;
  const buttonColorClass = hasModifiedSources
    ? 'text-orange-500 hover:text-orange-500'
    : 'text-primary hover:text-primary-hover';

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1 text-xs transition-colors',
              buttonColorClass,
              className,
            )}
          >
            {/* Will be re-added if users ignore the color change */}
            {/* {hasModifiedSources && <Info className="h-3.5 w-3.5" />} */}
            <span className="italic underline decoration-dotted">Sources</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-h-80 max-w-md overflow-y-auto p-3"
        >
          <div className="space-y-2">
            {citations.modifiedCitations.length > 0 && (
              <>
                <div className="mb-2 text-xs italic text-orange-600">
                  Note: These values differ from your original intake responses. Below is your
                  intake answer.
                </div>
                <CitationList
                  title="Modified Sources"
                  citations={citations.modifiedCitations}
                  variant="modified"
                />
              </>
            )}

            {citations.generalCitations.length > 0 && (
              <CitationList
                title="General Sources"
                citations={citations.generalCitations}
                variant="general"
              />
            )}

            {citations.partnerCitations.length > 0 && (
              <CitationList
                title="Partner Sources"
                citations={citations.partnerCitations}
                variant="general"
              />
            )}

            <div className="mt-2 border-t pt-2">
              <p className="text-xs text-gray-500">Data pre-filled from intake responses</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default QuestionCitationTooltip;
